import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Send, MapPin, Mail, ExternalLink } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

const LOGO = '/img/logo.png';

export default function Footer() {
  const { t, lang } = useLang();
  const f = t.footer;
  const nav = t.nav;

  const scrollToSection = (id) => {
    const element = document.querySelector(id);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer
      id="footer"
      className="relative py-20 md:py-24 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #0A0B0F 0%, #05060A 100%)' }}
      data-testid="footer"
    >
      {/* Background effects */}
      <div
        className="orb-gold absolute"
        style={{ width: 600, height: 600, top: '-30%', right: '-10%', opacity: 0.08 }}
      />
      <div
        className="orb-purple absolute"
        style={{ width: 500, height: 500, bottom: '-30%', left: '-10%', opacity: 0.06 }}
      />

      {/* Top border */}
      <div 
        className="absolute top-0 left-0 right-0 h-px"
        style={{ 
          background: 'linear-gradient(90deg, transparent 0%, rgba(212,175,55,0.4) 20%, rgba(107,78,255,0.4) 80%, transparent 100%)' 
        }}
      />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Main Content */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 mb-16">
          
          {/* Brand Section - 4 cols */}
          <div className="md:col-span-4 flex flex-col">
            <div className="mb-6">
              <img 
                src={LOGO} 
                alt="KidsMafia" 
                className="h-16 w-auto object-contain mb-5 icon-glow" 
              />
              <p className="text-[#E5E7EB] text-base leading-relaxed mb-4 font-medium">
                {f.tagline}
              </p>
              <div className="flex items-start gap-2.5">
                <MapPin size={18} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <span className="text-[#BFC3C9] text-sm leading-relaxed">{f.location}</span>
              </div>
            </div>
          </div>

          {/* Navigation - 3 cols */}
          <div className="md:col-span-3 flex flex-col">
            <h3 className="text-white font-bold text-lg mb-6 font-heading tracking-tight">
              {lang === 'ru' ? 'Навигация' : 'Navegación'}
            </h3>
            <nav className="flex flex-col gap-3">
              {[
                { label: nav.about, href: '#about' },
                { label: nav.format, href: '#format' },
                { label: nav.investments, href: '#investments' },
                { label: nav.gallery, href: '#gallery' },
              ].map((link, i) => (
                <button
                  key={i}
                  onClick={() => scrollToSection(link.href)}
                  className="text-[#BFC3C9] hover:text-[#D4AF37] transition-colors text-sm text-left group flex items-center gap-2"
                >
                  <span className="w-0 group-hover:w-4 h-px bg-[#D4AF37] transition-all duration-300" />
                  {link.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Contact - 5 cols */}
          <div className="md:col-span-5 flex flex-col gap-6">
            <h3 className="text-white font-bold text-lg mb-2 font-heading tracking-tight">
              {lang === 'ru' ? 'Контакты' : 'Contacto'}
            </h3>

            {/* Email */}
            <a
              href="mailto:kidsmafia.vlc@gmail.com"
              data-testid="footer-email"
              className="glass-premium rounded-2xl p-5 flex items-center gap-4 group hover:border-[#D4AF37]/40 transition-all duration-300"
            >
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300"
                style={{ 
                  background: 'linear-gradient(135deg, rgba(212,175,55,0.15) 0%, rgba(212,175,55,0.05) 100%)',
                  border: '1px solid rgba(212,175,55,0.3)'
                }}
              >
                <Mail size={20} className="text-[#D4AF37]" />
              </div>
              <div className="flex-1">
                <p className="text-[#9CA3AF] text-xs uppercase tracking-wider mb-1 font-semibold">
                  {f.email_label}
                </p>
                <p className="text-white font-bold text-base group-hover:text-[#D4AF37] transition-colors">
                  kidsmafia.vlc@gmail.com
                </p>
              </div>
              <ExternalLink size={16} className="text-[#9CA3AF] opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>

            {/* Phone */}
            <a
              href="tel:+34624586917"
              data-testid="footer-phone"
              className="glass-premium rounded-2xl p-5 flex items-center gap-4 group hover:border-[#D4AF37]/40 transition-all duration-300"
            >
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300"
                style={{ 
                  background: 'linear-gradient(135deg, rgba(212,175,55,0.15) 0%, rgba(212,175,55,0.05) 100%)',
                  border: '1px solid rgba(212,175,55,0.3)'
                }}
              >
                <Phone size={20} className="text-[#D4AF37]" />
              </div>
              <div className="flex-1">
                <p className="text-[#9CA3AF] text-xs uppercase tracking-wider mb-1 font-semibold">
                  {f.phone_label}
                </p>
                <p className="text-white font-bold text-base group-hover:text-[#D4AF37] transition-colors">
                  +34 624 586 917
                </p>
              </div>
              <ExternalLink size={16} className="text-[#9CA3AF] opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>

            {/* Telegram */}
            <a
              href="https://t.me/SerjValencia"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-telegram"
              className="glass-premium rounded-2xl p-5 flex items-center gap-4 group hover:border-[#229ED9]/40 transition-all duration-300"
            >
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300"
                style={{ 
                  background: 'linear-gradient(135deg, rgba(34,158,217,0.15) 0%, rgba(34,158,217,0.05) 100%)',
                  border: '1px solid rgba(34,158,217,0.3)'
                }}
              >
                <Send size={20} className="text-[#229ED9]" />
              </div>
              <div className="flex-1">
                <p className="text-[#9CA3AF] text-xs uppercase tracking-wider mb-1 font-semibold">
                  {f.telegram_label}
                </p>
                <p className="text-white font-bold text-base group-hover:text-[#229ED9] transition-colors">
                  @SerjValencia
                </p>
              </div>
              <ExternalLink size={16} className="text-[#9CA3AF] opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
          </div>
        </div>

        {/* Company Info Section */}
        <div 
          className="py-8 mb-8 border-t border-b"
          style={{ borderColor: 'rgba(255,255,255,0.06)' }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-white font-bold text-sm mb-3 uppercase tracking-wider">
                {f.company}
              </h4>
              <div className="space-y-2 text-[#BFC3C9] text-sm">
                <p>{f.address}</p>
                <p>{f.nif}</p>
              </div>
            </div>
            <div>
              <h4 className="text-white font-bold text-sm mb-3 uppercase tracking-wider">
                {f.legal_links}
              </h4>
              <nav className="flex flex-col gap-2">
                <Link 
                  to={`/${lang}/legal-notice`}
                  className="text-[#BFC3C9] hover:text-[#D4AF37] transition-colors text-sm"
                >
                  {f.legal_notice}
                </Link>
                <Link 
                  to={`/${lang}/privacy-policy`}
                  className="text-[#BFC3C9] hover:text-[#D4AF37] transition-colors text-sm"
                >
                  {f.privacy}
                </Link>
                <Link 
                  to={`/${lang}/cookies-policy`}
                  className="text-[#BFC3C9] hover:text-[#D4AF37] transition-colors text-sm"
                >
                  {f.cookies}
                </Link>
                <Link 
                  to={`/${lang}/terms-of-use`}
                  className="text-[#BFC3C9] hover:text-[#D4AF37] transition-colors text-sm"
                >
                  {f.terms}
                </Link>
              </nav>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div
          className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4"
          style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
        >
          <p className="text-[#9CA3AF] text-sm text-center sm:text-left">
            {f.rights}
          </p>
          
          {/* Brand accent dots */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-[#D4AF37] shadow-lg shadow-[#D4AF37]/40" />
              <div className="w-2 h-2 rounded-full bg-[#6B4EFF] shadow-lg shadow-[#6B4EFF]/40" />
            </div>
            <a 
              href="https://sites.dathent.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#9CA3AF] hover:text-[#D4AF37] text-xs font-medium tracking-wide transition-colors duration-300"
            >
              {lang === 'ru' ? 'Разработано ' : 'Desarrollado por '}
              <span className="font-semibold">Dathent Group</span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
